import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Button,
  DatePicker,
  Modal,
  Select,
  Spinner,
  Text,
  type TypeSelectOption,
  toastService,
} from '@thefarmersfront/kitchen';

import { FormField, ModalContentsContainer, ModalSection, Table } from '~/components/common';

import {
  useGetReturnDisposalCouriersQuery,
  useGetReturnDisposalInboundAreaQuery,
  useGetReturnDisposalIssueItemsQuery,
  useGetReturnDisposalOperationTimesQuery,
  usePutReturnDisposalRtsParcelMutation,
} from '~/hooks/receive';
import {
  applyRegisteredRtsParcelInvoiceErrors,
  clearRegisteredRtsParcelInvoiceErrors,
  clearResolvedRtsParcelDuplicateErrors,
  formatDate,
  getRegisteredRtsParcelInvoiceNos,
  getReturnDisposalIssueItemsColumns,
  hasRtsParcelInvoiceError,
  revalidateRtsParcelInvoices,
} from '~/utils';

import {
  RETURN_DISPOSAL_REQUEST_MESSAGE,
  RETURN_DISPOSAL_RTS_METHOD,
  RTS_PARCEL_INVOICE_MAX_COUNT,
  RTS_PARCEL_INVOICE_PATTERN,
  RTS_PARCEL_NOTICE,
  RTS_PARCEL_RECOVERY_TIME_TEXT,
  TOAST_MESSAGE,
} from '~/constants';

import ReceiveReturnDisposalReturnInvoiceGuideModal from '../ReceiveReturnDisposalReturnInvoiceGuideModal';
import RtsParcelInvoiceGroup from './RtsParcelInvoiceGroup';

import type { TypeReturnDisposalIssueItemApi, TypeRtsParcelInvoiceGroup } from '~/types/receive.types';

import styles from './ReceiveReturnDisposalRtsParcelModal.module.scss';

type TypeReceiveReturnDisposalRtsParcelModalProps = {
  isOpen: boolean;
  inspectionIds: number[];
  /** 선택 전건이 보류해제-회송 건인지 — 컬리 지정 회수예정일이 자동 노출되고 변경 불가 */
  isRtsPendingInput: boolean;
  onClose: () => void;
  /** 신청 성공 시(부모에서 목록 재조회 + 모달 닫기) */
  onSuccess: () => void;
};

/**
 * [입고관리 > 회송/폐기 관리] 회수(택배) 처리 신청 모달
 *
 * @note 상품정보 미리보기 + 회수정보(회수예정일·택배사·이슈카드별 송장번호)를 입력해 신청한다.
 *       택배는 시간을 고르지 않으므로 시간 필드는 지정 건 여부와 무관하게 종일(00:00~23:59) 고정 표시로 비활성 유지한다.
 *       신청 payload 는 선택한 날짜의 첫 슬롯 capaId 를 자동 지정해 전송하고, 슬롯에 capaId 가 없으면
 *       그 키를 생략한다(BE 가 지정 슬롯을 찾아 처리하므로 capaId 부재로 신청을 막지 않는다).
 *       보류해제-회송 건은 컬리가 지정한 슬롯의 날짜를 자동 표시하고 달력을 잠근다(그 슬롯의 capaId 가 null).
 *       송장번호는 카드별 다중 입력이며 형식·모달 내 중복은 입력 중 검증하고, 다른 이슈카드에 이미 등록된
 *       송장은 신청 실패 응답의 송장번호 목록으로 해당 행에만 인라인 에러를 표시한다.
 *       신청 요청 중에는 회수 정보 입력을 모두 잠가, 응답이 제출 당시와 다른 택배사·송장 조합에 반영되지 않게 한다.
 */
const ReceiveReturnDisposalRtsParcelModal = ({
  isOpen,
  inspectionIds,
  isRtsPendingInput,
  onClose,
  onSuccess,
}: TypeReceiveReturnDisposalRtsParcelModalProps) => {
  const invoiceIdRef = useRef(0);
  const nextInvoiceId = () => {
    invoiceIdRef.current += 1;
    return `invoice-${invoiceIdRef.current}`;
  };

  const [recoveryDate, setRecoveryDate] = useState<Date | null>(null);
  /** 선택 날짜의 입고가능시간 슬롯 ID (시간 선택 UI 없이 자동 지정, 신청 payload 전송값) */
  const [recoveryCapaId, setRecoveryCapaId] = useState<number | null>(null);
  const [courier, setCourier] = useState<TypeSelectOption<string> | null>(null);
  const [invoiceGroups, setInvoiceGroups] = useState<TypeRtsParcelInvoiceGroup[]>([]);
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  const issueItemsQuery = useGetReturnDisposalIssueItemsQuery({ inspectionIds, enabled: isOpen });
  const operationTimesQuery = useGetReturnDisposalOperationTimesQuery({
    inspectionIds,
    rtsMethod: RETURN_DISPOSAL_RTS_METHOD.PARCEL,
    enabled: isOpen,
  });
  const couriersQuery = useGetReturnDisposalCouriersQuery({ enabled: isOpen });
  const inboundAreaQuery = useGetReturnDisposalInboundAreaQuery({ inspectionIds, enabled: isOpen });
  const submitMutation = usePutReturnDisposalRtsParcelMutation();

  const issueItems = issueItemsQuery.data ?? [];
  const operationTimes = useMemo(() => operationTimesQuery.data ?? [], [operationTimesQuery.data]);

  // 상품정보 로드 시 이슈카드별 송장 입력 그룹을 1행씩 초기화(사용자 입력 유지 위해 최초 1회만)
  const isInitialized = useRef(false);
  useEffect(() => {
    if (!isInitialized.current && (issueItemsQuery.data?.length ?? 0) > 0) {
      isInitialized.current = true;
      setInvoiceGroups(
        (issueItemsQuery.data ?? []).map((item) => ({
          inspectionId: item.inspectionId,
          goodsName: item.goodsName,
          goodsCode: item.goodsCode,
          invoices: [{ id: nextInvoiceId(), value: '' }],
        }))
      );
    }
  }, [issueItemsQuery.data]);

  // 보류해제-회송 건은 서버가 컬리 지정 회수예정일의 슬롯(capaId null)만 내려준다.
  // 그 슬롯의 날짜를 자동 표시하고 달력은 잠가(disabled) 변경을 막는다.
  const [prevOperationTimes, setPrevOperationTimes] = useState<typeof operationTimes | null>(null);
  const [prevIsRtsPendingInput, setPrevIsRtsPendingInput] = useState<boolean | null>(null);
  if (operationTimes !== prevOperationTimes || isRtsPendingInput !== prevIsRtsPendingInput) {
    setPrevOperationTimes(operationTimes);
    setPrevIsRtsPendingInput(isRtsPendingInput);

    if (isRtsPendingInput && operationTimes.length > 0) {
      const [earliestDate] = operationTimes.map((slot) => slot.date).sort();
      const [year, month, day] = earliestDate.split('-').map(Number);

      setRecoveryDate(new Date(year, month - 1, day));
    }
  }

  // 상품정보·회수예정일·택배사 세 조회 모두 신청 폼 구성에 필요 → 로딩/에러를 합쳐 동일 상태 UI로 노출
  const isContentLoading = issueItemsQuery.isLoading || operationTimesQuery.isLoading || couriersQuery.isLoading;
  const isContentError = issueItemsQuery.isError || operationTimesQuery.isError || couriersQuery.isError;

  const columns = useMemo(() => getReturnDisposalIssueItemsColumns(), []);

  /** 회수장소: inbound-area 조회 주소, 미조회 시 '-' (정보성 필드 → 실패해도 모달은 사용 가능) */
  const locationText = inboundAreaQuery.data?.address || '-';

  /** 회수 가능 날짜 집합 (DatePicker 활성 날짜 판별) */
  const availableDateSet = useMemo(() => new Set(operationTimes.map((slot) => slot.date)), [operationTimes]);

  const courierOptions = useMemo<TypeSelectOption<string>[]>(
    () => (couriersQuery.data ?? []).map((code) => ({ value: code.code, text: code.name })),
    [couriersQuery.data]
  );

  // 신청 유효성: 날짜·택배사 선택 + 모든 카드의 모든 송장이 형식 통과 + 중복 없음
  const invoiceValues = invoiceGroups
    .flatMap((group) => group.invoices.map((invoice) => invoice.value.trim()))
    .filter((value) => value !== '');
  const hasDuplicateInvoice = new Set(invoiceValues).size !== invoiceValues.length;
  const isEveryInvoiceValid =
    invoiceGroups.length > 0 &&
    invoiceGroups.every(
      (group) =>
        group.invoices.length > 0 &&
        group.invoices.every((invoice) => RTS_PARCEL_INVOICE_PATTERN.test(invoice.value.trim()))
    );
  /** 신청 요청 진행 중 — 버튼과 함께 회수 정보 입력도 잠근다 */
  const isSubmitting = submitMutation.isPending;

  // 신청 유효성에 capaId 는 넣지 않는다 — 슬롯에 capaId 가 없으면 미전송하고 BE 가 지정 슬롯을 찾는다
  const isValid =
    !!recoveryDate &&
    !!courier &&
    isEveryInvoiceValid &&
    !hasDuplicateInvoice &&
    !hasRtsParcelInvoiceError(invoiceGroups) &&
    !isSubmitting;

  /**
   * 날짜만 선택하므로 해당 날짜의 슬롯 capaId 를 자동 지정한다.
   *
   * 같은 날짜에 슬롯이 여러 개(시간대 분할 + 동일 시간대의 용량 버킷) 내려올 수 있어
   * 응답 순서에 좌우되지 않도록 시작시각 → capaId 순으로 정렬해 가장 이른 슬롯을 고른다.
   */
  const handleChangeDate = (date: Date | null) => {
    setRecoveryDate(date);

    const selectedDate = formatDate(date, 'YYYY-MM-DD');
    const daySlots = selectedDate ? operationTimes.filter((operationTime) => operationTime.date === selectedDate) : [];
    const [earliestSlot] = daySlots.sort(
      (a, b) => a.startTime.localeCompare(b.startTime) || (a.capaId ?? 0) - (b.capaId ?? 0)
    );

    setRecoveryCapaId(earliestSlot?.capaId ?? null);
  };

  const handleChangeInvoice = (inspectionId: number, invoiceId: string, value: string) => {
    setInvoiceGroups((prev) => {
      const updated = prev.map((group) =>
        group.inspectionId !== inspectionId
          ? group
          : {
              ...group,
              invoices: group.invoices.map((invoice) =>
                invoice.id !== invoiceId ? invoice : { ...invoice, value, error: undefined }
              ),
            }
      );

      return clearResolvedRtsParcelDuplicateErrors(updated);
    });
  };

  const handleBlurInvoice = () => {
    setInvoiceGroups((prev) => revalidateRtsParcelInvoices(prev));
  };

  const handleChangeCourier = (option: TypeSelectOption<string> | null) => {
    setCourier(option);
    setInvoiceGroups((prev) => clearRegisteredRtsParcelInvoiceErrors(prev));
  };

  const handleAddInvoice = (inspectionId: number) => {
    setInvoiceGroups((prev) =>
      prev.map((group) =>
        group.inspectionId !== inspectionId || group.invoices.length >= RTS_PARCEL_INVOICE_MAX_COUNT
          ? group
          : { ...group, invoices: [...group.invoices, { id: nextInvoiceId(), value: '' }] }
      )
    );
  };

  const handleRemoveInvoice = (inspectionId: number, invoiceId: string) => {
    setInvoiceGroups((prev) =>
      revalidateRtsParcelInvoices(
        prev.map((group) =>
          group.inspectionId !== inspectionId
            ? group
            : {
                ...group,
                invoices:
                  group.invoices.length > 1
                    ? group.invoices.filter((invoice) => invoice.id !== invoiceId)
                    : group.invoices,
              }
        )
      )
    );
  };

  const handleSubmit = () => {
    if (!recoveryDate || !courier) return;

    const invoices = invoiceGroups.map((group) => ({
      inspectionId: group.inspectionId,
      invoiceNos: group.invoices.map((invoice) => invoice.value.trim()),
    }));

    submitMutation.mutate(
      { ...(recoveryCapaId !== null && { capaId: recoveryCapaId }), courier: courier.value, invoices },
      {
        onSuccess: () => {
          toastService.addToast({ message: RETURN_DISPOSAL_REQUEST_MESSAGE.RTS_PARCEL_SUBMIT_SUCCESS });
          onSuccess();
        },
        onError: (error) => {
          // 다른 이슈카드에 이미 등록된 송장은 해당 행에 인라인으로 안내한다. 응답 송장번호가 입력과 하나도 맞지 않으면 토스트로 대체한다.
          const registeredInvoiceNos = getRegisteredRtsParcelInvoiceNos(error);
          const { matchedCount } = applyRegisteredRtsParcelInvoiceErrors(invoiceGroups, registeredInvoiceNos);
          if (matchedCount > 0) {
            // 요청 중에는 입력이 잠겨 있어 제출 당시 값과 같지만, 반영은 항상 최신 상태 위에 에러만 붙인다
            setInvoiceGroups((prev) => applyRegisteredRtsParcelInvoiceErrors(prev, registeredInvoiceNos).groups);
            return;
          }

          toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
        },
      }
    );
  };

  return (
    <>
      <Modal
        title="회수(택배) 처리신청"
        size="xlarge"
        open={isOpen}
        confirmText="신청하기"
        disabled={!isValid}
        onCancel={onClose}
        onConfirm={handleSubmit}
      >
        {isContentLoading ? (
          <div className={styles['state-area']}>
            <Spinner />
          </div>
        ) : isContentError ? (
          <div className={styles['state-area']}>
            <Text size="large" color="tertiary">
              정보를 불러오지 못했습니다.
            </Text>
          </div>
        ) : (
          <ModalContentsContainer className={styles['modal-body']}>
            <div className={styles['notice-box']}>
              <div className={styles['notice-content']}>
                <span className={styles['notice-title']}>{RTS_PARCEL_NOTICE.TITLE}</span>
                <ul className={styles['notice-list']}>
                  <li>{RTS_PARCEL_NOTICE.NO_RECEIPT}</li>
                  <li>{`${RTS_PARCEL_NOTICE.LOCATION_PREFIX}${locationText}`}</li>
                  <li>{RTS_PARCEL_NOTICE.NO_DOCUMENT}</li>
                </ul>
              </div>
              <Button
                className={styles['notice-guide']}
                size="medium"
                variant="contained"
                color="primary"
                onClick={() => setIsGuideOpen(true)}
              >
                반송장 작성 가이드
              </Button>
            </div>

            <ModalSection title="상품 정보">
              <Table<TypeReturnDisposalIssueItemApi>
                className={styles['preview-table']}
                data={issueItems}
                columns={columns}
                rowHeight={40}
                maxHeight="220px"
                loading={issueItemsQuery.isLoading}
                error={issueItemsQuery.isError}
              />
            </ModalSection>

            <ModalSection title="회수 정보">
              <FormField
                label="회수예정일"
                required
                tooltip={{
                  description: isRtsPendingInput
                    ? RETURN_DISPOSAL_REQUEST_MESSAGE.RECOVERY_DATE_FIXED_TOOLTIP
                    : RETURN_DISPOSAL_REQUEST_MESSAGE.RECOVERY_DATE_TOOLTIP,
                }}
                contents={
                  <div className={styles['date-time-inputs']}>
                    <DatePicker
                      size="small"
                      placeholder="날짜 선택"
                      maxWidth="229px"
                      portalTarget={document.body}
                      shouldCloseOnNotInView
                      disabled={isRtsPendingInput || isSubmitting}
                      value={recoveryDate}
                      isDateDisabled={(date) => {
                        const formattedDate = formatDate(date, 'YYYY-MM-DD');
                        return formattedDate ? !availableDateSet.has(formattedDate) : false;
                      }}
                      onChange={handleChangeDate}
                    />
                    <Select
                      size="small"
                      placeholder={RTS_PARCEL_RECOVERY_TIME_TEXT}
                      maxWidth="317px"
                      disabled
                      value={null}
                      options={[]}
                    />
                  </div>
                }
              />
              <FormField
                label="택배사"
                required
                contents={
                  <Select
                    size="small"
                    placeholder="선택"
                    maxWidth="229px"
                    dropdownPortalTarget={document.body}
                    shouldCloseOnNotInView
                    disabled={isSubmitting}
                    value={courier}
                    options={courierOptions}
                    onChange={(_: unknown, option: TypeSelectOption<string> | null) =>
                      handleChangeCourier(option ?? null)
                    }
                  />
                }
              />
              <FormField
                label="송장번호"
                required
                contents={
                  <div className={styles['invoice-groups']}>
                    {invoiceGroups.map((group) => (
                      <RtsParcelInvoiceGroup
                        key={group.inspectionId}
                        group={group}
                        isDisabled={isSubmitting}
                        onChangeInvoice={handleChangeInvoice}
                        onBlurInvoice={handleBlurInvoice}
                        onAddInvoice={handleAddInvoice}
                        onRemoveInvoice={handleRemoveInvoice}
                      />
                    ))}
                  </div>
                }
              />
            </ModalSection>
          </ModalContentsContainer>
        )}
      </Modal>

      {isGuideOpen && (
        <ReceiveReturnDisposalReturnInvoiceGuideModal isOpen={isGuideOpen} onClose={() => setIsGuideOpen(false)} />
      )}
    </>
  );
};

export default ReceiveReturnDisposalRtsParcelModal;
